import axios from "axios";
import { random } from "Utils/Strings";
import { forceInt } from "Utils";
// circular import allows us to mock internal functions when testing
import * as Self from "./Auth";
import * as LocalBag from "./LocalStorage";

const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID;
const baseUrl = process.env.REACT_APP_OAUTH_SERVER;
const appDomain = process.env.REACT_APP_DOMAIN;

export function userCan(user, acl, action = null) {
    if (user.is_admin) {
        return true;
    }
    if (acl === "admin" || acl === "Administrator") {
        return user._hydratedRoles.hasOwnProperty("Administrator");
    }

    let resource = acl[0].toUpperCase() + acl.slice(1);
    let roles = Object.keys(user._hydratedRoles);

    for (let i = 0; i < roles.length; i++) {
        const role = roles[i];
        if (role === "Administrator") {
            return true;
        }

        if (!user._hydratedRoles[role]?.hasOwnProperty(resource)) {
            continue;
        }

        const accesses = user._hydratedRoles[role][resource];
        for (let j = 0; j < accesses.length; j++) {
            const access = accesses[j];
            if (
                (access.action === action || access.action === "*") &&
                forceInt(access.allowed) === 1
            ) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Returns user response.
 * Response is not wrapped in a user transformer.
 * @returns {Promise<string>}
 */
export async function getMe() {
    let response;
    try {
        response = await axios.get(
            process.env.REACT_APP_OAUTH_SERVER + "/api/v2/me",
            {
                headers: {
                    Authorization: "Bearer " + getOauthToken(),
                },
            }
        );
    } catch (e) {
        return 401;
    }

    if (!response || response.status !== 200) {
        return 500;
    }

    return response.data;
}

/**
 * Request a new access token.
 * @returns {Promise<string>}
 */
export async function requestAccessToken(exchangeToken, checkState) {
    if (!checkState || checkState !== Self.getOauthCheckState()) {
        throw new Error("failed check state");
    }
    const params = {
        client_id: clientId,
        grant_type: "authorization_code",
        redirect_uri: encodeURIComponent(appDomain + "/authorize"),
        code: exchangeToken,
        code_verifier: Self.getOauthChallengeState(),
    };
    let response;

    try {
        response = await axios.post(
            baseUrl + "/auth/v2/access-token",
            new URLSearchParams(params),
            { withCredentials: false }
        );
    } catch (e) {
        throw new Error("failed authorization");
    }

    if (!response || response.status !== 200) {
        throw new Error("response not ok");
    }

    Self.setOauthToken(response.data.access_token);
    Self.setOauthRefreshToken(response.data.refresh_token);

    return response.data.access_token;
}

/**
 * Refreshes access token with refresh token
 * @returns {Promise<string>}
 */
export async function requestRefreshToken() {
    let response;
    console.info("Refreshing token...");

    const params = {
        client_id: clientId,
        grant_type: "refresh_token",
        redirect_uri: encodeURIComponent(appDomain + "/authorize"),
        refresh_token: Self.getOauthRefreshToken(),
    };

    try {
        response = await axios.post(
            baseUrl + "/auth/v2/access-token",
            new URLSearchParams(params),
            { withCredentials: false }
        );
    } catch (e) {
        if (e.request.status) {
            throw new Error("failed refresh");
        } else {
            throw new Error("server unavailable");
        }
    }

    if (!response || response.status !== 200) {
        console.log(response);
        throw new Error("response not ok");
    }

    console.info("Token refreshed.");

    setOauthToken(response.data.access_token);
    setOauthRefreshToken(response.data.refresh_token);

    return response.data.access_token;
}

export function getOauthToken() {
    return LocalBag.getLocalStorageItem("token");
}

export function setOauthToken(token) {
    LocalBag.setLocalStorageItem("token", token);
}

export function removeOauthToken() {
    LocalBag.removeLocalStorageItem("token");
}

export function getOauthRefreshToken() {
    return LocalBag.getLocalStorageItem("refreshToken");
}

export function setOauthRefreshToken(token) {
    LocalBag.setLocalStorageItem("refreshToken", token);
}

export function removeOauthRefreshToken() {
    LocalBag.removeLocalStorageItem("refreshToken");
}

export function setNewOauthCheckState() {
    const checkState = random(32);
    LocalBag.setLocalStorageItem("checkState", checkState);
    return checkState;
}
export function getOauthCheckState() {
    return LocalBag.getLocalStorageItem("checkState");
}

export function setNewOauthChallengeState() {
    const challengeState = random(50);
    LocalBag.setLocalStorageItem("challengeState", challengeState);
    return challengeState;
}
export function getOauthChallengeState() {
    return LocalBag.getLocalStorageItem("challengeState");
}

export function getPublicationUuid() {
    return LocalBag.getLocalStorageItem("publicationUuid");
}
export function setPublicationUuid(uuid) {
    LocalBag.setLocalStorageItem("publicationUuid", uuid);
}
export function removePublicationUuid() {
    LocalBag.removeLocalStorageItem("publicationUuid");
}
export function getPublicationCode() {
    return LocalBag.getLocalStorageItem("PublicationCode");
}
export function setPublicationCode(code) {
    LocalBag.setLocalStorageItem("PublicationCode", code);
}
export function removePublicationCode() {
    LocalBag.removeLocalStorageItem("PublicationCode");
}

export function getOauthUrl(continueTo = "/app/dashboard") {
    const newCheckState = setNewOauthCheckState();
    const newChallenge = setNewOauthChallengeState();

    let params = {
        client_id: clientId,
        redirect_uri: encodeURIComponent(appDomain + "/authorize"),
        state: newCheckState,
        code_challenge: newChallenge,
        scope: "",
        response_type: "code",
        approval_prompt: "auto",
        continue: continueTo,
    };

    return baseUrl + "/auth/v2/authorize?" + new URLSearchParams(params);
}
